import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Rocket, Play, Square, RotateCw, ExternalLink,
  Trash2, Check, X, Eye, EyeOff, Copy, Lock, Plus, RefreshCw,
  ChevronDown, KeyRound, FileText, Database, HardDrive, AlertTriangle, Download,
} from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../auth.jsx";
import { actionLabel } from "../lib/eventLabels.js";
import { StatusPill, Spinner, Button, Mono, timeAgo } from "../components/ui.jsx";
import { SettingsSection, SettingsRow, AnchorNav } from "../components/SettingsSection.jsx";
import ConfirmDelete from "../components/ConfirmDelete.jsx";
import MoveToProject from "../components/MoveToProject.jsx";
import AddDomainModal from "../components/AddDomainModal.jsx";
import BillingGateModal from "../components/BillingGateModal.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import ResourceChangeModal from "../components/ResourceChangeModal.jsx";
import AddDiskModal from "../components/AddDiskModal.jsx";
import GitSourcePicker from "../components/GitSourcePicker.jsx";
import DnsSetup from "../components/DnsSetup.jsx";
import { fmtHourly } from "../lib/money.js";

const TABS = ["Deployments", "Logs", "Metrics", "Environment", "Events", "Settings"];

// Service-notification pref <-> dropdown label (backend stores the pref value).
const NOTIFY_LABEL = { default: "Workspace default", failures: "Failures only", off: "Off" };
const NOTIFY_PREF = { "Workspace default": "default", "Failures only": "failures", Off: "off" };

export default function ServiceDetail() {
  const { id } = useParams();
  const [svc, setSvc] = useState(null);
  const [tab, setTab] = useState("Deployments");
  const [deploys, setDeploys] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [gate, setGate] = useState(null); // { code, message } when a deploy is billing-blocked (402)

  useEffect(() => { api.me().then(setUser).catch(() => {}); }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null); setSvc(null); setDeploys(null);
    Promise.all([api.service(id), api.deployments(id)])
      .then(([service, deploymentList]) => {
        if (cancelled) return;
        setSvc(service);
        setDeploys(deploymentList);
      })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [id]);

  // A deploy is live when any listed deploy is still building/queued.
  const deploying = (deploys || []).some((d) => /in_progress|queued|building/i.test(d.status || ""));

  // While a deploy runs, poll the list so the spinner starts AND clears on its own.
  useEffect(() => {
    if (!deploying) return;
    const t = setInterval(() => api.deployments(id).then(setDeploys).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [deploying, id]);

  async function action(kind, arg) {
    setBusy(true);
    try {
      if (kind === "deploy") await api.deploy(id);
      else if (kind === "deploy-cache") await api.deploy(id, { clearCache: true });
      else if (kind === "deploy-commit") await api.rollback(id, arg); // deploy a specific commit
      else await api.control(id, kind);
      api.deployments(id).then(setDeploys);
    } catch (e) {
      // Deploy blocked by a gate. Account-wide refusals (account setup, credit, billing,
      // suspension) are already being explained by the app shell's modal; plan_required is
      // about THIS service, so it opens here with a jump to the Scale settings.
      if (e.gateHandled) { /* shown globally */ }
      else if (e.status === 402 && e.code) setGate({ code: e.code, message: e.message });
      else alert(e.message);
    } finally { setBusy(false); }
  }

  if (!svc) {
    if (error) return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="card">
          <p className="text-sm font-medium" style={{ color: "var(--text)" }}>Unable to load service</p>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>{error.message}</p>
          <Link to="/" className="mt-4 inline-flex text-sm" style={{ color: "var(--accent)" }}>Back to services</Link>
        </div>
      </div>
    );
    return (
      <div className="flex h-64 items-center justify-center" style={{ color: "var(--text-muted)" }}>
        <Spinner className="mr-2" /> Loading…
      </div>
    );
  }

  const running = svc.status === "running";
  const healthy = svc.health === "healthy";
  // Both branches of the old ternary returned "hel-prod-1 · Helsinki", so every service
  // without a region reported Helsinki whether or not it ran there — a fabricated fact in
  // the meta row, and one a customer with data-residency requirements would rely on.
  // "hel-prod-1" is an internal host name besides. Say nothing rather than guess.
  const region = svc.region || null;
  const runtimeLabel = runtimeName(svc.runtime);

  return (
    <div className="page">
      {/* back link */}
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] transition-colors"
        style={{ color: "var(--text-muted)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
      >
        <ArrowLeft className="h-[15px] w-[15px]" /> Services
      </Link>

      {/* ── header ── */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-5">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <h1
              className="text-2xl font-semibold"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: "-0.01em", color: "var(--text)" }}
            >
              {svc.name}
            </h1>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
              style={{
                background: running ? "var(--ok-soft)" : "var(--neutral-soft)",
                color: running ? "var(--ok-text)" : "var(--neutral-text)",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: running ? "var(--ok)" : "var(--text-muted)" }} />
              {running ? `running · ${healthy ? "healthy" : svc.health || "healthy"}` : (svc.status || "unknown")}
            </span>
          </div>
          {/* The customer's own domains. The platform hostname lives on the Settings tab;
              it is shown here only when the service has no custom domain yet. */}
          {(() => {
            const hosts = svc.customDomains?.length ? svc.customDomains : svc.domain ? [svc.domain] : [];
            return hosts.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {hosts.map((host) => (
                  <a
                    key={host}
                    href={`https://${host}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[13.5px] font-medium transition-colors"
                    style={{ color: "var(--accent-text)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                  >
                    {host}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            );
          })()}
        </div>

        {/* action buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          <DeployMenu
            busy={busy || deploying}
            onLatest={() => action("deploy")}
            onSpecific={() => {
              const c = window.prompt("Commit SHA to deploy:");
              if (c && c.trim()) action("deploy-commit", c.trim());
            }}
            onClearCache={() => action("deploy-cache")}
            onRestart={() => action("restart")}
          />
          {svc.status === "stopped" ? (
            <Button variant="secondary" onClick={() => action("start")} disabled={busy}>
              <Play className="h-3.5 w-3.5" /> Start
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => action("stop")} disabled={busy}>
              <Square className="h-3.5 w-3.5" /> Stop
            </Button>
          )}
        </div>
      </div>

      {/* ── meta row ── */}
      <div
        className="flex flex-wrap items-center gap-x-[18px] gap-y-2 pb-[18px] pt-[3px] text-[12.5px]"
        style={{ color: "var(--text-muted)" }}
      >
        <Mono>{svc.repo || "—"}{svc.branch ? ` · ${svc.branch}` : ""}</Mono>
        {runtimeLabel && <span>{runtimeLabel}</span>}
        {region && <span>{region}</span>}
        <span>Last deploy {timeAgo(svc.lastDeployedAt)}</span>
      </div>

      {/* ── underline tab bar ── */}
      <div className="tab-bar mb-[22px]">
        {TABS.map((t) => {
          const active = tab === t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="-mb-px border-b-2 px-[14px] py-[10px] text-[13.5px] font-semibold transition-colors"
              style={active
                ? { borderColor: "var(--accent)", color: "var(--text)" }
                : { borderColor: "transparent", color: "var(--text-muted)" }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "var(--text)"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              {t}
            </button>
          );
        })}
      </div>

      {tab === "Deployments" && (
        <Deployments deploys={deploys} serviceId={id} repo={svc.repo} onRedeploy={() => action("deploy")} onDeploysChange={setDeploys} />
      )}
      {tab === "Logs" && <LogsTab serviceId={id} name={svc.name} />}
      {tab === "Metrics" && <MetricsTab serviceId={id} />}
      {tab === "Environment" && <EnvironmentTab serviceId={id} onDeploy={() => action("deploy")} />}
      {tab === "Events" && <EventsTab serviceId={id} />}
      {tab === "Settings" && <SettingsTab svc={svc} serviceId={id} region={region} onDeploy={() => action("deploy")} deployBusy={busy} onRename={(name) => setSvc((s) => ({ ...s, name }))} onResourcesChanged={(resources) => setSvc((s) => ({ ...s, resources }))} />}

      {gate && (
        <BillingGateModal
          gate={gate}
          onClose={() => setGate(null)}
          onGoToSettings={() => setTab("Settings")}
        />
      )}
    </div>
  );
}

// Render-style "Manual Deploy" split menu: latest / specific commit / clear cache / restart.
function DeployMenu({ busy, onLatest, onSpecific, onClearCache, onRestart }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const items = [
    { label: "Deploy latest commit", fn: onLatest },
    { label: "Deploy a specific commit", fn: onSpecific },
    { label: "Clear build cache & deploy", fn: onClearCache },
    { label: "Restart service", fn: onRestart },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Button variant="primary" onClick={() => setOpen((o) => !o)} disabled={busy}>
        {busy ? <Spinner /> : <Rocket className="h-[15px] w-[15px]" />} Manual Deploy
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 40, minWidth: 232,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "var(--shadow-lg)", overflow: "hidden",
        }}>
          {items.map((it, i) => (
            <button
              key={it.label}
              onClick={() => { setOpen(false); it.fn(); }}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
                fontSize: 13, background: "transparent", border: "none",
                borderTop: i ? "1px solid var(--border)" : "none", color: "var(--text)", cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Unknown is a real answer. This returned "Node 20" for null AND for every unrecognised
// runtime, so a Python or Go service was confidently mislabelled in the meta row.
// The version was invented too — nothing here knows which Node the image actually has.
function runtimeName(runtime) {
  if (!runtime) return null;
  const r = String(runtime).toLowerCase();
  if (r.includes("node")) return "Node";
  if (r === "nixpacks") return "Nixpacks (auto-detected)";
  if (r.includes("docker")) return "Docker";
  return runtime;
}

// ── Deployments tab ────────────────────────────────────────────────────────────

// Repo → the GitHub base for linking commits (git@…:owner/repo.git or https URL).
function githubBase(repo) {
  const m = String(repo || "").match(/github\.com[:/]([^/\s]+\/[^/\s]+?)(?:\.git)?$/i);
  return m ? `https://github.com/${m[1]}` : null;
}

function Deployments({ deploys, serviceId, repo, onRedeploy, onDeploysChange }) {
  const ghBase = githubBase(repo);
  async function rollback(d) {
    if (!d.commit) return;
    if (!window.confirm(`Roll back to commit ${d.commit}?\n\nThis will redeploy the service at that commit.`)) return;
    // Through the shared client: a refused rollback used to fail silently here.
    try { await api.rollback(serviceId, d.commit); }
    catch (e) { if (!e.gateHandled) alert(e.message); return; }
    setTimeout(() => api.deployments(serviceId).then(onDeploysChange), 1500);
  }

  if (!deploys) return (
    <div className="text-sm" style={{ color: "var(--text-muted)" }}>
      <Spinner className="mr-2 inline" /> Loading deployments…
    </div>
  );

  const isOk = (d) => /success|finished|running|healthy|live/i.test(d.status || "");
  const isFail = (d) => /fail|error/i.test(d.status || "");
  const deploying = deploys.some((d) => /in_progress|queued|building/i.test(d.status || ""));
  let liveSeen = false;

  return (
    <div>
      <div className="mb-3.5 flex items-center justify-between">
        <span className="text-[13px]" style={{ color: "var(--text-muted)" }}>
          {deploying ? "Deploying…" : `Showing latest ${deploys.length} deploy${deploys.length === 1 ? "" : "s"}`}
        </span>
        <Button variant="secondary" onClick={onRedeploy} disabled={deploying}>
          {deploying ? <Spinner /> : <RotateCw className="h-3.5 w-3.5" />} {deploying ? "Deploying…" : "Redeploy latest"}
        </Button>
      </div>

      <div
        className="overflow-hidden rounded-lg border"
        style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}
      >
        {deploys.length === 0 && (
          <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No deployments yet.</div>
        )}
        {deploys.map((d, i) => {
          const ok = isOk(d);
          const fail = isFail(d);
          const live = ok && !liveSeen && !fail;
          if (live) liveSeen = true;
          const canRollback = ok && !live && d.commit;
          return (
            <div
              key={d.uuid ?? i}
              className="flex items-center gap-[14px] px-[18px] py-[14px]"
              style={i !== 0 ? { borderTop: "1px solid var(--border)" } : {}}
            >
              <span
                className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg"
                style={fail
                  ? { background: "var(--err-soft)", color: "var(--err-text)" }
                  : { background: "var(--ok-soft)", color: "var(--ok-text)" }}
              >
                {fail ? <X className="h-3.5 w-3.5" strokeWidth={2.6} /> : <Check className="h-3.5 w-3.5" strokeWidth={2.6} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold" style={{ color: "var(--text)" }}>
                  {d.message || "—"}
                </div>
                <div className="mt-[3px] text-[11.5px]" style={{ color: "var(--text-muted)" }}>
                  {d.commit && ghBase ? (
                    <a href={`${ghBase}/commit/${d.commit}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className="hover:underline" style={{ color: "var(--accent-text)" }}
                       title={`View commit ${d.commit} on GitHub`}>
                      <Mono>{d.commit}</Mono>
                    </a>
                  ) : <Mono>{d.commit || "—"}</Mono>}
                  {" · "}<Mono>{d.branch || "main"}</Mono> · {d.trigger || "manual"}
                </div>
              </div>
              <span
                className="min-w-[58px] text-right text-xs"
                style={{ color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}
              >
                {d.durationSec != null ? `${d.durationSec}s` : "—"}
              </span>
              <span className="min-w-[78px] text-right text-xs" style={{ color: "var(--text-muted)" }}>
                {timeAgo(d.startedAt)}
              </span>
              <div className="flex min-w-[96px] justify-end">
                {live && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-[11px] py-1 text-[11.5px] font-semibold"
                    style={{ background: "var(--ok-soft)", color: "var(--ok-text)" }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--ok)" }} /> Live
                  </span>
                )}
                {canRollback && (
                  <button
                    onClick={() => rollback(d)}
                    className="rounded-md border px-3 py-[5px] text-xs font-semibold transition-colors"
                    style={{ borderColor: "var(--border-strong)", background: "var(--surface)", color: "var(--text)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface)")}
                  >
                    Rollback
                  </button>
                )}
                {fail && <span className="text-xs font-semibold" style={{ color: "var(--err-text)" }}>Failed</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Logs tab (Render-style terminal — dark in both themes) ──────────────────────

const LVL_STYLE = { INFO: "#6ea8fe", LOG: "#7c8696", OK: "#34d77a", WARN: "#f5b945", ERROR: "#f87171" };

// ISO timestamp → HH:MM:SS (local); "" if missing/unparseable.
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toTimeString().slice(0, 8);
}

function LogsTab({ serviceId, name }) {
  const [lines, setLines] = useState(null);
  const [wrap, setWrap] = useState(false);
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [tail, setTail] = useState(true);
  const [source, setSource] = useState("runtime"); // "runtime" (container) | "build" (last deploy)
  const [err, setErr] = useState(null);
  const boxRef = useRef(null);

  // Runtime: live-tail the container (re-fetch every 4s). Build: one-shot fetch of the
  // last deployment's logs from Coolify's DB (this is where a failed build's error is).
  useEffect(() => {
    let cancelled = false;
    setLines(null); setErr(null);
    if (source === "build") {
      api.buildLogs(serviceId).then((d) => {
        if (cancelled) return;
        setErr(d?.error || null);
        setLines((d?.lines || []).map((l) => ({ time: l.time, level: l.type === "stderr" ? "ERROR" : "LOG", message: l.message })));
      }).catch((e) => { if (!cancelled) { setErr(e.message); setLines([]); } });
      return () => { cancelled = true; };
    }
    const load = () =>
      api.logs(serviceId).then((data) => { if (!cancelled) setLines(data); }).catch(() => { if (!cancelled) setLines((l) => l || []); });
    load();
    if (!tail) return () => { cancelled = true; };
    const t = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [serviceId, tail, source]);

  const q = query.trim().toLowerCase();
  const shown = (lines || []).filter((l) => !q || String(l.message ?? "").toLowerCase().includes(q));

  // Auto-scroll to bottom on new lines while tailing.
  useEffect(() => {
    if (tail && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [shown.length, tail]);

  function copy() {
    const text = shown.map((l) => `${fmtTime(l.time)} ${l.level || ""} ${l.message ?? ""}`.trim()).join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const tools = "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-[5px] text-[11.5px] font-medium transition-colors";

  return (
    <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-strong)", boxShadow: "var(--shadow)" }}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-[9px]" style={{ background: "#13161d", borderBottom: "1px solid #232a36" }}>
        <div className="flex items-center gap-[9px]">
          <div className="inline-flex overflow-hidden rounded-md border" style={{ borderColor: "#2a323f" }}>
            {["runtime", "build"].map((s) => (
              <button
                key={s}
                onClick={() => setSource(s)}
                className="px-2.5 py-[4px] text-[11px] font-semibold"
                style={{ background: source === s ? "#1c212b" : "transparent", color: source === s ? "#cdd6e4" : "#7c8696" }}
                title={s === "build" ? "Build/deploy logs from the last deployment" : "Live logs from the running container"}
              >
                {s === "build" ? "Build" : "Runtime"}
              </button>
            ))}
          </div>
          {source === "runtime" ? (
            <button
              onClick={() => setTail((v) => !v)}
              className="inline-flex items-center gap-[7px] text-xs font-semibold"
              style={{ color: tail ? "#cfe9d6" : "#7c8696" }}
              title={tail ? "Pause live tail" : "Resume live tail"}
            >
              <span className="h-[7px] w-[7px] rounded-full" style={{ background: tail ? "#34d77a" : "#4a5261", animation: tail ? "dd-pulse 1.4s infinite" : "none" }} />
              Live tail {tail ? "on" : "off"}
            </button>
          ) : (
            <span className="mono text-[11.5px]" style={{ color: "#7c8696" }}>last deploy</span>
          )}
          <span className="mono text-[11.5px]" style={{ color: "#7c8696" }}>{name} · {source === "build" ? "build" : "stdout"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search logs…"
            className="mono rounded-md border px-2.5 py-[5px] text-[11.5px] outline-none"
            style={{ borderColor: "#2a323f", background: "#0b0e14", color: "#cdd6e4", width: "160px" }}
          />
          <button
            onClick={copy}
            className={tools}
            style={{ borderColor: "#2a323f", background: "transparent", color: copied ? "#34d77a" : "#aeb6c2" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#1c212b")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {copied ? <Check className="h-[13px] w-[13px]" /> : <Copy className="h-[13px] w-[13px]" />} {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={() => setWrap((w) => !w)}
            className={tools}
            style={{ borderColor: "#2a323f", background: wrap ? "#1c212b" : "transparent", color: "#aeb6c2" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#1c212b")}
            onMouseLeave={(e) => (e.currentTarget.style.background = wrap ? "#1c212b" : "transparent")}
          >
            Wrap
          </button>
        </div>
      </div>

      <div
        ref={boxRef}
        className="mono h-[380px] overflow-y-auto px-2 py-2 text-[12px]"
        style={{ background: "#0b0e14", lineHeight: "1.7" }}
      >
        {!lines && (
          <div className="px-2" style={{ color: "#5b6678" }}><Spinner className="mr-2 inline" /> Fetching logs…</div>
        )}
        {err && (
          <div className="px-2 py-2" style={{ color: "#f6b6b6" }}>Couldn’t load build logs: {err}</div>
        )}
        {lines && !err && shown.length === 0 && (
          <div className="px-2 py-8 text-center" style={{ color: "#5b6678" }}>
            {q ? `No log lines match “${query.trim()}”.` : source === "build" ? "No build logs for the last deployment." : "No logs yet — output appears here once the service produces some."}
          </div>
        )}
        {shown.map((l, i) => {
          const t = fmtTime(l.time);
          const lvl = l.level || "";
          const isErr = lvl === "ERROR";
          return (
            <div
              key={i}
              className="flex gap-3 rounded px-2 py-[1px]"
              style={{
                whiteSpace: wrap ? "pre-wrap" : "pre",
                background: isErr ? "rgba(248,113,113,0.08)" : undefined,
                borderLeft: isErr ? "2px solid #f87171" : "2px solid transparent",
              }}
            >
              <span className="shrink-0" style={{ color: "#5b6678", width: "58px" }}>{t || "—"}</span>
              {lvl && <span className="shrink-0 font-semibold" style={{ color: LVL_STYLE[lvl] || "#cdd6e4", width: "46px" }}>{lvl}</span>}
              <span style={{ color: isErr ? "#f6b6b6" : "#cdd6e4" }}>{l.message}</span>
            </div>
          );
        })}
        {lines && tail && shown.length > 0 && (
          <div className="mt-0.5 flex items-center gap-3 px-2">
            <span className="inline-block h-[15px] w-2" style={{ background: "#34d77a", animation: "dd-pulse 1.1s steps(1) infinite" }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Metrics tab (Render-style time-series graphs: CPU / Memory / Network) ─────────

const RANGES = ["1h", "6h", "24h"];

// Humanise a byte count → "67.4 MB". null → "—".
function fmtBytes(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let x = Math.abs(n), i = 0;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i += 1; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

// Hand-rolled SVG area+line sparkline. No charting dep. Scales to its own max
// (10% headroom); stroke stays crisp via non-scaling-stroke under the stretched
// viewBox. A dashed peak gridline marks the window high. Empty → nothing.
function MetricChart({ values, color, peak }) {
  const W = 600, H = 140;
  if (!values || values.length === 0) return <div style={{ height: H / 2 }} />;
  const vs = values.length === 1 ? [values[0], values[0]] : values;
  const max = Math.max(peak ?? 0, ...vs, 1e-9) * 1.1 || 1;
  const x = (i) => (i / (vs.length - 1)) * W;
  const y = (v) => H - (v / max) * H;
  const line = vs.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const area = `${line} L ${W} ${H} L 0 ${H} Z`;
  const gid = `grad-${color.replace(/[^a-z]/gi, "")}`;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" opacity="0.5" />
      ))}
      {peak != null && peak > 0 && (
        <line x1="0" x2={W} y1={y(peak)} y2={y(peak)} stroke={color} strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" opacity="0.55" />
      )}
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.75" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ChartCard({ label, big, sub, values, color, peak }) {
  return (
    <div className="rounded-lg border px-[18px] pt-4 pb-2" style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}>
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
        {sub && <div className="mono text-[11.5px]" style={{ color: "var(--text-muted)" }}>{sub}</div>}
      </div>
      <div className="mt-1 text-[26px] font-semibold" style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{big}</div>
      <div className="mt-2.5"><MetricChart values={values} color={color} peak={peak} /></div>
    </div>
  );
}

function RangeToggle({ value, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border" style={{ borderColor: "var(--border)" }}>
      {RANGES.map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className="px-2.5 py-1 text-[12px] font-semibold"
          style={{
            background: value === r ? "var(--accent)" : "var(--surface)",
            color: value === r ? "#fff" : "var(--text-muted)",
          }}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function MetricsTab({ serviceId }) {
  const [window, setWindow] = useState("1h");
  const [data, setData] = useState(null);   // null = loading; else { series, stats } | { error }
  const [host, setHost] = useState(null);   // host capacity (admin only; null if unavailable)

  useEffect(() => {
    let cancelled = false;
    setData(null);
    const load = () => {
      api.metricsHistory(serviceId, window)
        .then((d) => { if (!cancelled) setData(d || { stats: null }); })
        .catch((e) => { if (!cancelled) setData({ error: e.message, stats: null }); });
      api.metricsHost(window).then((h) => { if (!cancelled) setHost(h?.stats ? h : null); }).catch(() => { if (!cancelled) setHost(null); });
    };
    load();
    const t = setInterval(load, 15000); // graph refreshes as new samples land
    return () => { cancelled = true; clearInterval(t); };
  }, [serviceId, window]);

  const s = data?.stats;
  const series = data?.series || { cpu: [], mem: [], net: [], throughput: [], diskio: [], pids: [] };
  const rate = (n) => `${fmtBytes(n)}/s`;

  return (
    <div>
      <div className="mb-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h4 className="text-[13.5px] font-semibold" style={{ color: "var(--text)" }}>Application Metrics</h4>
          <span className="inline-flex items-center gap-[7px] text-[11.5px] font-semibold" style={{ color: "var(--ok-text)" }}>
            <span className="h-[7px] w-[7px] rounded-full" style={{ background: "var(--ok)", animation: "dd-pulse 1.4s infinite" }} />
            Live
          </span>
          {s?.uptimePct != null && (
            <span className="rounded-md px-2 py-0.5 text-[11.5px] font-semibold" style={{ background: "var(--surface-2)", color: s.uptimePct >= 99 ? "var(--ok-text)" : "var(--text-muted)" }}>
              {s.uptimePct}% uptime
            </span>
          )}
        </div>
        <RangeToggle value={window} onChange={setWindow} />
      </div>

      {!data ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading metrics…</div>
      ) : data.error ? (
        <div className="rounded-lg border px-4 py-10 text-center text-[13px]" style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text-muted)", boxShadow: "var(--shadow)" }}>
          Metrics unavailable — {data.error}
        </div>
      ) : !s ? (
        <div className="rounded-lg border px-4 py-10 text-center text-[13px]" style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text-muted)", boxShadow: "var(--shadow)" }}>
          Accumulating metrics… graphs fill in as samples are collected (about a minute after the service starts running).
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ChartCard
            label="CPU"
            big={`${s.cpu.current}%`}
            sub={`peak ${s.cpu.peak}% · avg ${s.cpu.avg}%`}
            values={series.cpu.map((p) => p.v)}
            peak={s.cpu.peak}
            color="var(--accent)"
          />
          <ChartCard
            label="Memory"
            big={`${s.mem.current}%`}
            sub={`${fmtBytes(s.mem.bytes)} · peak ${s.mem.peak}% · avg ${s.mem.avg}%`}
            values={series.mem.map((p) => p.v)}
            peak={s.mem.peak}
            color="var(--ok)"
          />
          <ChartCard
            label="Network throughput"
            big={rate(s.throughput?.current || 0)}
            sub={`peak ${rate(s.throughput?.peak || 0)} · avg ${rate(s.throughput?.avg || 0)}`}
            values={(series.throughput || []).map((p) => p.v)}
            peak={s.throughput?.peak}
            color="#8b5cf6"
          />
          <ChartCard
            label="Disk I/O"
            big={rate((series.diskio?.at(-1)?.v) || 0)}
            sub={`peak ${fmtBytes(s.diskio?.peak || 0)}`}
            values={(series.diskio || []).map((p) => p.v)}
            peak={s.diskio?.peak}
            color="#f59e0b"
          />
          <ChartCard
            label="Processes"
            big={`${s.pids?.current ?? 0}`}
            sub={`peak ${s.pids?.peak ?? 0} · avg ${s.pids?.avg ?? 0}`}
            values={(series.pids || []).map((p) => p.v)}
            peak={s.pids?.peak}
            color="#14b8a6"
          />
          {host?.stats && (
            <div className="rounded-lg border px-[18px] py-4" style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Host capacity (the box)</div>
              <div className="grid gap-4 sm:grid-cols-3">
                <HostStat label="CPU" pct={host.stats.cpu.current} />
                <HostStat label="Memory" pct={host.stats.mem.current} sub={`${fmtBytes(host.stats.mem.bytes)} / ${fmtBytes(host.stats.mem.total)}`} />
                <HostStat label="Disk" pct={host.stats.disk.current} sub={`${fmtBytes(host.stats.disk.bytes)} / ${fmtBytes(host.stats.disk.total)}`} warn={host.stats.disk.current >= 85} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HostStat({ label, pct, sub, warn }) {
  const color = warn ? "var(--err-text)" : pct >= 75 ? "#f59e0b" : "var(--ok)";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</span>
        <span className="text-[15px] font-semibold" style={{ color, fontVariantNumeric: "tabular-nums" }}>{pct ?? 0}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "var(--surface-2)" }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct || 0)}%`, background: color }} />
      </div>
      {sub && <div className="mt-1 mono text-[11px]" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

// ── Environment tab ─────────────────────────────────────────────────────────────

// Strong random secret: 32-char base64url. ponytail: getRandomValues, no crypto lib.
function genSecret() {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Parse pasted .env text → [{key,value}] (ignore blanks/#comments, split on first =, strip quotes).
function parseEnvText(text) {
  return text.split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#") && l.includes("="))
    .map((l) => {
      const idx = l.indexOf("=");
      return { key: l.slice(0, idx).trim(), value: l.slice(idx + 1).trim().replace(/^["']|["']$/g, "") };
    })
    .filter((r) => r.key);
}

// Migration check — flags env values that break after moving off a PaaS: leftover
// *.onrender.com URLs, provider-internal DB/Redis hosts, and RENDER_*-style vars.
// Silent when the env is clean, so it costs nothing on native services.
const SEV = {
  high:   { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c", label: "Fix" },
  medium: { bg: "#fffbeb", border: "#fde68a", text: "#b45309", label: "Update" },
  low:    { bg: "var(--surface)", border: "var(--border)", text: "var(--text-muted)", label: "Review" },
};
function MigrationCheck({ serviceId }) {
  const [warnings, setWarnings] = useState(null);
  useEffect(() => {
    let off = false;
    api.envScan(serviceId).then((d) => { if (!off) setWarnings(d?.warnings || []); }).catch(() => { if (!off) setWarnings([]); });
    return () => { off = true; };
  }, [serviceId]);
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="mb-3.5 rounded-lg border p-4" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: "#92400e" }}>
        <AlertTriangle size={15} /> Migration check — {warnings.length} value{warnings.length > 1 ? "s" : ""} need attention
      </div>
      <div className="flex flex-col gap-1.5">
        {warnings.map((w) => {
          const s = SEV[w.severity] || SEV.low;
          return (
            <div key={w.key} className="flex items-start gap-2 rounded-md border px-3 py-2 text-[13px]"
                 style={{ background: s.bg, borderColor: s.border }}>
              <span className="mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
                    style={{ background: s.text, color: "#fff" }}>{s.label}</span>
              <div style={{ minWidth: 0 }}>
                <span className="mono font-semibold">{w.key}</span>
                <span className="ml-2" style={{ color: "var(--text-muted)" }}>{w.message}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EnvironmentTab({ serviceId, onDeploy }) {
  const [envs, setEnvs] = useState(null);
  const [baseline, setBaseline] = useState({});   // uuid → serialized "key\0value" at last load/save
  const [groups, setGroups] = useState(null);     // every org variable group (each with .services)
  const [groupBusy, setGroupBusy] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [shown, setShown] = useState({});         // uuid → true when its secret value is revealed
  const [menu, setMenu] = useState(null);         // null | "root" | "datastore"
  const [paste, setPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [databases, setDatabases] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);   // { ok, text }
  const [exportOpen, setExportOpen] = useState(false);

  // Reload both lists: attaching a group writes its keys straight into the
  // service's env, so the variable table below must be refetched with it.
  async function reloadEnvs() {
    const d = await api.envs(serviceId);
    const rows = (d || []).map((e) => ({ ...e }));
    setEnvs(rows);
    setBaseline(snapshot(rows));
  }
  // Every group in the org (each carries its .services list) — one call powers
  // both the attached chips and the "attach" menu. 403s for a member with no org.
  const reloadGroups = () =>
    api.varGroups().then((d) => setGroups(Array.isArray(d) ? d : [])).catch(() => setGroups([]));

  useEffect(() => {
    let cancelled = false;
    api.envs(serviceId).then((d) => {
      if (cancelled) return;
      const rows = (d || []).map((e) => ({ ...e }));
      setEnvs(rows);
      setBaseline(snapshot(rows));
    });
    api.varGroups().then((d) => { if (!cancelled) setGroups(Array.isArray(d) ? d : []); }).catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, [serviceId]);

  async function toggleGroup(group, attach) {
    setGroupBusy(true);
    try {
      if (attach) await api.attachVarGroup(group.id, serviceId);
      else await api.detachVarGroup(group.id, serviceId);
      await Promise.all([reloadGroups(), reloadEnvs()]);
      setSaveMsg({ ok: true, text: `${group.name} ${attach ? "attached" : "detached"} — redeploy to apply.` });
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setGroupBusy(false);
      setAttachOpen(false);
    }
  }

  // build a key\0value map keyed by uuid for dirty comparison
  function snapshot(rows) {
    return Object.fromEntries(rows.map((r) => [r.uuid, `${r.key}\0${r.value ?? ""}`]));
  }

  // a row is dirty if new (uuid starts "new-") or its key/value differs from baseline
  function isDirtyRow(r) {
    return String(r.uuid).startsWith("new-") || baseline[r.uuid] !== `${r.key}\0${r.value ?? ""}`;
  }
  const dirty = !!envs && envs.some((r) => isDirtyRow(r) && r.key.trim());

  // keys (trimmed, case-sensitive) that appear on more than one row → every row in the group is flagged
  const dupKeys = (() => {
    const counts = {};
    (envs || []).forEach((r) => { const k = r.key.trim(); if (k) counts[k] = (counts[k] || 0) + 1; });
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  })();
  const hasDups = dupKeys.size > 0;

  function addRow(row) {
    setEnvs((e) => [...(e || []), { uuid: "new-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6), key: "", value: "", is_secret: false, ...row }]);
    setSaveMsg(null);
  }

  function editRow(uuid, patch) {
    setEnvs((e) => e.map((r) => (r.uuid === uuid ? { ...r, ...patch } : r)));
    setSaveMsg(null);
  }

  // Reveal a secret's value. Coolify's API never returns values, so the list ships
  // secrets blank + revealable; fetch the plaintext from our encrypted store on the
  // first reveal, drop it into the row, and rebaseline so viewing ≠ editing.
  async function toggleReveal(e) {
    const willShow = !shown[e.uuid];
    if (willShow && (e.value ?? "") === "" && e.revealable && !String(e.uuid).startsWith("new-")) {
      try {
        const r = await api.revealEnv(serviceId, e.key);
        if (r?.revealable && r.value != null) {
          setEnvs((rows) => rows.map((x) => (x.uuid === e.uuid ? { ...x, value: r.value } : x)));
          setBaseline((b) => ({ ...b, [e.uuid]: `${e.key}\0${r.value}` }));
        }
      } catch { /* leave blank on failure */ }
    }
    setShown((s) => ({ ...s, [e.uuid]: willShow }));
  }

  // Reveal every secret at once: fetch each blank+revealable value, rebaseline, show all.
  async function revealAll() {
    const secrets = (envs || []).filter((e) => e.is_secret);
    const toFetch = secrets.filter((e) => (e.value ?? "") === "" && e.revealable && !String(e.uuid).startsWith("new-"));
    const results = await Promise.all(
      toFetch.map((e) => api.revealEnv(serviceId, e.key).then((r) => ({ e, r })).catch(() => null))
    );
    const vals = {};
    for (const it of results) if (it?.r?.revealable && it.r.value != null) vals[it.e.uuid] = it.r.value;
    if (Object.keys(vals).length) {
      setEnvs((rows) => rows.map((x) => (vals[x.uuid] != null ? { ...x, value: vals[x.uuid] } : x)));
      setBaseline((b) => {
        const n = { ...b };
        for (const e of secrets) if (vals[e.uuid] != null) n[e.uuid] = `${e.key}\0${vals[e.uuid]}`;
        return n;
      });
    }
    setShown(Object.fromEntries(secrets.map((e) => [e.uuid, true])));
  }

  async function remove(envId) {
    setEnvs((e) => e.filter((x) => x.uuid !== envId));
    if (!String(envId).startsWith("new-")) api.deleteEnv(serviceId, envId).catch(() => {});
  }

  function importPaste() {
    parseEnvText(pasteText).forEach((r) => addRow(r));
    setPasteText("");
    setPaste(false);
  }

  function openDatastore() {
    setMenu("datastore");
    if (databases == null) api.databases().then((d) => setDatabases(Array.isArray(d) ? d : [])).catch(() => setDatabases([]));
  }

  async function pickDatabase(db) {
    setMenu(null);
    try {
      const detail = await api.database(db.uuid);
      addRow({ key: "DATABASE_URL", value: detail?.internalUrl || "" });
    } catch {
      addRow({ key: "DATABASE_URL", value: "" });
    }
  }

  // Save every row with a non-empty key that changed (or is new). Returns true on success.
  async function save() {
    setSaving(true); setSaveMsg(null);
    try {
      for (const r of envs) {
        if (r.key.trim() && isDirtyRow(r)) {
          await api.saveEnv(serviceId, { key: r.key.trim(), value: r.value ?? "", is_secret: !!r.is_secret });
        }
      }
      setBaseline(snapshot(envs));
      setSaveMsg({ ok: true, text: "Saved." });
      return true;
    } catch (err) {
      setSaveMsg({ ok: false, text: err.message || "Save failed" });
      return false;
    } finally { setSaving(false); }
  }

  async function saveAndRedeploy() {
    if (await save()) {
      setSaveMsg({ ok: true, text: "Saved — redeploying…" });
      onDeploy?.();
    }
  }

  // Serialize env rows to .env text. Quote values with whitespace/special chars (and
  // escape the quote/backslash/$/backtick inside) so the file re-imports cleanly.
  function serializeEnv(rows) {
    return rows
      .filter((r) => r.key.trim())
      .map((r) => {
        const v = r.value ?? "";
        const needsQuote = v === "" ? false : /[\s#'"$`\\]/.test(v);
        return `${r.key.trim()}=${needsQuote ? `"${v.replace(/(["\\$`])/g, "\\$1")}"` : v}`;
      })
      .join("\n") + "\n";
  }

  // Export the full env (secrets included) as a .env — copy to clipboard or download.
  // Secret values are blank in the list until revealed, so fetch each from the store
  // first; falls back gracefully on any that can't be revealed.
  async function exportEnv(mode) {
    setExportOpen(false);
    const rows = envs || [];
    const need = rows.filter((r) => r.is_secret && (r.value ?? "") === "" && r.revealable && !String(r.uuid).startsWith("new-"));
    const fetched = {};
    await Promise.all(need.map((r) =>
      api.revealEnv(serviceId, r.key).then((x) => { if (x?.value != null) fetched[r.uuid] = x.value; }).catch(() => {})
    ));
    const text = serializeEnv(rows.map((r) => ({ ...r, value: fetched[r.uuid] != null ? fetched[r.uuid] : (r.value ?? "") })));
    if (mode === "copy") {
      try { await navigator.clipboard.writeText(text); setSaveMsg({ ok: true, text: "Copied .env to clipboard" }); }
      catch { setSaveMsg({ ok: false, text: "Clipboard blocked — use Download instead" }); }
    } else {
      const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = ".env";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setSaveMsg({ ok: true, text: "Downloaded .env" });
    }
  }

  const inputCell = "mono w-full rounded-md border border-transparent bg-transparent px-2.5 py-[7px] text-[12px] outline-none transition-colors focus:border-[var(--accent)]";

  return (
    <div>
      {/* attached variable groups */}
      <div
        className="mb-4 rounded-lg border px-[18px] py-[15px]"
        style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}
      >
        <div className="mb-[13px] flex items-start justify-between gap-3">
          <div>
            <h4 className="text-[13.5px] font-semibold" style={{ color: "var(--text)" }}>Attached variable groups</h4>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              A group's keys are written into this service's variables below. Manage the groups themselves under Variable Groups.
            </p>
          </div>
          <div className="relative">
            <Button
              variant="secondary"
              disabled={groupBusy || !groups}
              onClick={() => setAttachOpen((o) => !o)}
            >
              <Plus className="h-3.5 w-3.5" /> Attach group
            </Button>
            {attachOpen && (
              <div
                className="absolute right-0 z-30 mt-1.5 w-60 rounded-lg border p-1.5"
                style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow-lg)" }}
              >
                {(groups || []).filter((g) => !g.services.includes(serviceId)).length === 0 ? (
                  <p className="px-2 py-1.5 text-[12.5px]" style={{ color: "var(--text-muted)" }}>
                    No other groups available.
                  </p>
                ) : (
                  (groups || [])
                    .filter((g) => !g.services.includes(serviceId))
                    .map((g) => (
                      <button
                        key={g.id}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-[var(--surface-2)]"
                        style={{ color: "var(--text)" }}
                        onClick={() => toggleGroup(g, true)}
                      >
                        <span className="mono truncate">{g.name}</span>
                        <span style={{ color: "var(--text-muted)" }}>{g.vars.length}</span>
                      </button>
                    ))
                )}
              </div>
            )}
          </div>
        </div>
        {!groups && <p className="text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading groups…</p>}
        {groups && !groups.some((g) => g.services.includes(serviceId)) && (
          <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>No variable groups attached.</p>
        )}
        {groups && groups.some((g) => g.services.includes(serviceId)) && (
          <div className="flex flex-wrap gap-2">
            {groups.filter((g) => g.services.includes(serviceId)).map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[12.5px]"
                style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-md" style={{ background: "var(--accent-soft)" }}>
                  <Lock className="h-3 w-3" style={{ color: "var(--accent-text)" }} />
                </span>
                <span className="mono font-semibold">{g.name}</span>
                <span style={{ color: "var(--text-muted)" }}>· {g.vars.length} var{g.vars.length === 1 ? "" : "s"}</span>
                <button
                  title={`Detach ${g.name} (removes its keys from this service)`}
                  disabled={groupBusy}
                  onClick={() => toggleGroup(g, false)}
                  style={{ color: "var(--text-muted)" }}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* service own variables toolbar */}
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2.5">
        <span className="text-[13px]" style={{ color: "var(--text-muted)" }}>
          {envs ? `Service variables · ${envs.length} keys, injected at build & runtime` : "Service variables"}
        </span>
        <div className="flex gap-2">
          {(() => {
            const anyShown = !!envs && envs.some((e) => e.is_secret && shown[e.uuid]);
            return (
              <Button
                variant="secondary"
                onClick={() => (anyShown ? setShown({}) : revealAll())}
              >
                {anyShown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {anyShown ? "Hide values" : "Reveal values"}
              </Button>
            );
          })()}

          {/* Export .env — copy or download (secrets revealed on the fly) */}
          <div className="relative">
            <Button variant="secondary" onClick={() => setExportOpen((o) => !o)} disabled={!envs || envs.length === 0}>
              <Download className="h-3.5 w-3.5" /> Export <ChevronDown className="h-3.5 w-3.5" />
            </Button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div
                  className="absolute right-0 z-20 mt-1.5 w-[200px] overflow-hidden rounded-lg border py-1 text-[13px]"
                  style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}
                >
                  <MenuItem icon={Copy} label="Copy to clipboard" onClick={() => exportEnv("copy")} />
                  <MenuItem icon={FileText} label="Download .env" onClick={() => exportEnv("download")} />
                </div>
              </>
            )}
          </div>

          {/* + Add variable dropdown (Render-style) */}
          <div className="relative">
            <Button variant="primary" onClick={() => setMenu((m) => (m ? null : "root"))}>
              <Plus className="h-3.5 w-3.5" /> Add variable <ChevronDown className="h-3.5 w-3.5" />
            </Button>
            {menu && (
              <>
                {/* click-away backdrop */}
                <div className="fixed inset-0 z-10" onClick={() => setMenu(null)} />
                <div
                  className="absolute right-0 z-20 mt-1.5 w-[230px] overflow-hidden rounded-lg border py-1 text-[13px]"
                  style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}
                >
                  {menu === "root" && (
                    <>
                      <MenuItem icon={Plus} label="Add a variable" onClick={() => { addRow({}); setMenu(null); }} />
                      <MenuItem icon={KeyRound} label="Generated secret" onClick={() => { addRow({ value: genSecret(), is_secret: true }); setMenu(null); }} />
                      <MenuItem icon={FileText} label="Import from .env" onClick={() => { setPaste(true); setMenu(null); }} />
                      <MenuItem icon={Database} label="Datastore URL" chevron onClick={openDatastore} />
                    </>
                  )}
                  {menu === "datastore" && (
                    <>
                      <MenuItem icon={ArrowLeft} label="Back" onClick={() => setMenu("root")} />
                      <div className="my-1 border-t" style={{ borderColor: "var(--border)" }} />
                      {databases == null && <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading…</div>}
                      {databases && databases.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>No databases found.</div>}
                      {databases && databases.map((db) => (
                        <MenuItem key={db.uuid} icon={Database} label={db.name || db.uuid} onClick={() => pickDatabase(db)} />
                      ))}
                      <p className="px-3 pb-1.5 pt-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                        Password isn't exposed by Coolify — you may need to fill it in.
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {paste && (
        <div className="mb-3.5 rounded-lg border p-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"KEY=value\nANOTHER=foo"}
            rows={5}
            className="mono input w-full"
            style={{ resize: "vertical" }}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setPaste(false); setPasteText(""); }}>Cancel</Button>
            <Button variant="primary" onClick={importPaste} disabled={!pasteText.trim()}>
              Import variables
            </Button>
          </div>
        </div>
      )}

      <MigrationCheck serviceId={serviceId} />

      {/* env table */}
      <div
        className="overflow-hidden rounded-lg border"
        style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow)" }}
      >
        <div
          className="grid items-center gap-3 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider"
          style={{ gridTemplateColumns: "1fr 1.5fr 40px", background: "var(--surface-2)", borderBottom: "1px solid var(--border)", color: "var(--text-muted)" }}
        >
          <span>Key</span><span>Value</span><span />
        </div>

        {!envs && (
          <div className="px-4 py-6 text-sm" style={{ color: "var(--text-muted)" }}>
            <Spinner className="mr-2 inline" /> Loading variables…
          </div>
        )}

        {envs && envs.length === 0 && (
          <div className="px-4 py-6 text-center text-[12.5px]" style={{ color: "var(--text-muted)" }}>
            No variables yet. Use “Add variable” to create one.
          </div>
        )}

        {envs && envs.map((e) => {
          const secret = !!e.is_secret;
          const maskValue = secret && !shown[e.uuid];
          const isDup = dupKeys.has(e.key.trim());
          return (
            <div
              key={e.uuid}
              className="grid items-center gap-3 border-t px-4 py-[9px]"
              style={{
                gridTemplateColumns: "1fr 1.5fr 40px",
                borderColor: "var(--border)",
                background: isDup ? "var(--err-soft)" : (isDirtyRow(e) && e.key.trim() ? "var(--surface-2)" : undefined),
                borderLeft: isDup ? "3px solid var(--err)" : "3px solid transparent",
              }}
            >
              <div>
                <input
                  value={e.key}
                  onChange={(ev) => editRow(e.uuid, { key: ev.target.value.toUpperCase() })}
                  placeholder="KEY"
                  className={inputCell}
                  style={{ color: "var(--text)", fontWeight: 500 }}
                />
                {isDup && <span className="px-2.5 text-[10.5px] font-semibold" style={{ color: "var(--err-text)" }}>duplicate key</span>}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={e.value ?? ""}
                  onChange={(ev) => editRow(e.uuid, { value: ev.target.value })}
                  placeholder="value"
                  type={maskValue ? "password" : "text"}
                  className={inputCell}
                  style={{ color: "var(--text-muted)" }}
                />
                {secret && (
                  <button
                    type="button"
                    onClick={() => toggleReveal(e)}
                    title={maskValue ? "Reveal value" : "Hide value"}
                    className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md transition-colors"
                    style={{ color: "var(--text-muted)", background: "transparent" }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "var(--surface-2)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                  >
                    {maskValue ? <Eye className="h-[15px] w-[15px]" /> : <EyeOff className="h-[15px] w-[15px]" />}
                  </button>
                )}
                <label className="flex shrink-0 items-center gap-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                  <input
                    type="checkbox"
                    checked={secret}
                    onChange={(ev) => editRow(e.uuid, { is_secret: ev.target.checked })}
                    style={{ accentColor: "var(--accent)" }}
                  />
                  secret
                </label>
              </div>
              <button
                onClick={() => remove(e.uuid)}
                title="Remove"
                className="flex h-[30px] w-[30px] items-center justify-center rounded-md transition-colors"
                style={{ color: "var(--text-muted)", background: "transparent" }}
                onMouseEnter={(ev) => { ev.currentTarget.style.background = "var(--err-soft)"; ev.currentTarget.style.color = "var(--err-text)"; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = "transparent"; ev.currentTarget.style.color = "var(--text-muted)"; }}
              >
                <Trash2 className="h-[15px] w-[15px]" />
              </button>
            </div>
          );
        })}
      </div>

      {/* save controls */}
      <div className="mt-3.5 flex flex-wrap items-center justify-end gap-3">
        {hasDups && (
          <span className="text-xs font-semibold" style={{ color: "var(--err-text)" }}>
            Duplicate key{dupKeys.size > 1 ? "s" : ""}: {[...dupKeys].join(", ")} — resolve before saving.
          </span>
        )}
        {saveMsg && <span className="text-xs" style={{ color: saveMsg.ok ? "var(--ok-text)" : "var(--err-text)" }}>{saveMsg.text}</span>}
        <Button variant="secondary" onClick={save} disabled={!dirty || saving || hasDups}>
          {saving ? <Spinner /> : <Check className="h-3.5 w-3.5" />} Save
        </Button>
        <Button variant="primary" onClick={saveAndRedeploy} disabled={!dirty || saving || hasDups}>
          {saving ? <Spinner /> : <Rocket className="h-3.5 w-3.5" />} Save &amp; Redeploy
        </Button>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, chevron }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors"
      style={{ color: "var(--text)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
      <span className="flex-1 truncate">{label}</span>
      {chevron && <ChevronDown className="h-3.5 w-3.5 -rotate-90" style={{ color: "var(--text-muted)" }} />}
    </button>
  );
}

// ── Events tab ─────────────────────────────────────────────────────────────────

function EventsTab({ serviceId }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.serviceEvents(serviceId)
      .then((data) => { if (!cancelled) setEvents(data); })
      .catch((e) => { if (!cancelled) setErr(e); });
    return () => { cancelled = true; };
  }, [serviceId]);

  if (err) return (
    <div className="card text-sm" style={{ color: "var(--err)" }}>Failed to load events: {err.message}</div>
  );
  if (!events) return (
    <div className="text-sm" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading events…</div>
  );
  if (events.length === 0) return (
    <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No events recorded yet.</div>
  );

  return (
    <div className="relative pl-2">
      {events.map((ev, i) => {
        const down = ev.action === "service.down";
        const up = ev.action === "service.up";
        const dotColor = down ? "var(--err)" : up ? "var(--ok)" : "var(--accent)";
        const dotRing = down ? "var(--err-soft)" : up ? "var(--ok-soft)" : "var(--accent-soft)";
        const labelColor = down ? "var(--err-text)" : up ? "var(--ok-text)" : "var(--text)";
        const actor = ev.actor_name || ev.actor_email || "system";
        const last = i === events.length - 1;
        return (
          <div key={ev.id ?? i} className="relative flex gap-4 pb-[22px]">
            <div className="flex shrink-0 flex-col items-center">
              <span
                className="mt-[3px] h-[11px] w-[11px] shrink-0 rounded-full"
                style={{ background: dotColor, boxShadow: `0 0 0 3px ${dotRing}`, border: "2px solid var(--surface)" }}
              />
              {!last && <span className="mt-1 min-h-[18px] w-0.5 flex-1" style={{ background: "var(--border)" }} />}
            </div>
            <div className="-mt-[3px]">
              <div className="flex items-center gap-2.5">
                <span className="text-[13.5px] font-semibold" style={{ color: labelColor }}>{actionLabel(ev.action)}</span>
                {ev.detail && <span className="text-xs" style={{ color: "var(--text-muted)" }}>{ev.detail}</span>}
              </div>
              <div className="mt-1 text-[11.5px]" style={{ color: "var(--text-muted)" }}>{actor} · {timeAgo(ev.created_at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Settings tab ───────────────────────────────────────────────────────────────

const NAV = [
  { id: "general", label: "General" },
  { id: "build", label: "Build" },
  { id: "deploy", label: "Deploy" },
  { id: "domains", label: "Custom Domains" },
  { id: "object-storage", label: "Object Storage" },
  { id: "pr-previews", label: "PR Previews" },
  { id: "networking", label: "Networking" },
  { id: "edge-caching", label: "Edge Caching" },
  { id: "notifications", label: "Notifications" },
  { id: "log-stream", label: "Log Stream" },
  { id: "health", label: "Health Checks" },
  { id: "disk", label: "Disk" },
  { id: "danger", label: "Delete or suspend" },
];

function SettingsTab({ svc, serviceId, region, onDeploy, deployBusy, onRename, onResourcesChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const navItems = NAV;

  // build & deploy (wired to /build; backend may 404 until added)
  const [branch, setBranch] = useState(svc.branch || "main");
  const [repo, setRepo] = useState(svc.repo || "");
  const [rootDir, setRootDir] = useState(svc.rootDirectory || "");
  const [buildCmd, setBuildCmd] = useState(svc.buildCommand || "");
  const [startCmd, setStartCmd] = useState(svc.startCommand || "");
  const [preDeploy, setPreDeploy] = useState(svc.preDeployCommand || "");
  const [buildBusy, setBuildBusy] = useState(false);
  const [buildMsg, setBuildMsg] = useState(null);

  // custom domain (Render-style wizard modal)
  const [domainModal, setDomainModal] = useState(false);
  const [domainsRefresh, setDomainsRefresh] = useState(0);
  const [listedFreeHost, setListedFreeHost] = useState(null); // the `free` row of the domains list

  // health check (wired to /build alongside commands; falls through if unsupported)
  const [healthPath, setHealthPath] = useState(svc.healthCheckPath || "/");

  // auto-deploy (wired to /auto-deploy; skipped by the push webhook when "Off")
  const [autoDeploy, setAutoDeploy] = useState(svc.autoDeploy === false ? "Off" : "On commit");
  async function changeAutoDeploy(v) {
    setAutoDeploy(v);
    await fetch(`/api/services/${serviceId}/auto-deploy`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: v !== "Off" }),
    }).catch(() => {});
  }

  // service notifications (wired to /notifications; label <-> pref value)
  const [svcNotify, setSvcNotify] = useState(NOTIFY_LABEL[svc.notifyPref] || "Workspace default");
  async function changeSvcNotify(label) {
    setSvcNotify(label);
    await fetch(`/api/services/${serviceId}/notifications`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pref: NOTIFY_PREF[label] || "default" }),
    }).catch(() => {});
  }

  // display-only local state (no backend)
  const [prPreview, setPrPreview] = useState("Off");
  const [edgeCache, setEdgeCache] = useState("Static assets");
  const [previewNotify, setPreviewNotify] = useState("Off");
  const [hookRevealed, setHookRevealed] = useState(false);
  const [hookCopied, setHookCopied] = useState(false);
  const [included, setIncluded] = useState([]);
  const [ignored, setIgnored] = useState([]);

  const deployHook = `https://app.debutdepoly.com/deploy/hook/${serviceId}?key=whsec_${serviceId.slice(0, 8)}`;

  async function saveBuild(e) {
    e?.preventDefault();
    setBuildBusy(true);
    setBuildMsg(null);
    try {
      const r = await fetch(`/api/services/${serviceId}/build`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          branch: branch.trim() || undefined,
          // Only sent when actually repointed — the server rejects a malformed URL, and
          // an unchanged value has no business being rewritten on every build save.
          repo: repo.trim() && repo.trim() !== (svc.repo || "") ? repo.trim() : undefined,
          rootDirectory: rootDir.trim() || undefined,
          buildCommand: buildCmd.trim(),
          startCommand: startCmd.trim(),
          preDeployCommand: preDeploy.trim() || undefined,
          healthCheckPath: healthPath.trim() || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || String(r.status));
      setBuildMsg({ ok: true, text: "Saved." });
    } catch (err) {
      setBuildMsg({ ok: false, text: err.message });
    } finally { setBuildBusy(false); }
  }

  const [confirmDel, setConfirmDel] = useState(false);
  async function deleteSvc() {
    const res = await fetch(`/api/services/${serviceId}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error || `Delete failed (${res.status})`);
    }
    navigate("/");
  }

  // anchor nav — track visible section
  const [activeSection, setActiveSection] = useState("general");
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActiveSection(vis[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    NAV.forEach((n) => { const el = document.getElementById(n.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  function jump(secId) {
    document.getElementById(secId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(secId);
  }

  // The free platform hostname this service is ACTUALLY bound to: the `free` row the server
  // lists, else its system hostname, else its primary domain when that is a platform one.
  // Never invented from the service name — a guessed hostname can point at the wrong server.
  const freeHost = listedFreeHost || svc.systemDomain
    || (/\.debutdepoly\.com$/i.test(svc.domain || "") ? svc.domain : null);

  return (
    <div className="flex gap-8">
      <div className="flex min-w-0 flex-1 flex-col gap-[18px]">

        {/* 1 · General */}
        <SettingsSection id="general" title="General">
          <SettingsRow label="Name" desc="A friendly name for this service.">
            <EditableName
              value={svc.name}
              onSave={(name) => api.renameService(serviceId, name).then(() => onRename(name))}
            />
          </SettingsRow>
          <SettingsRow label="Region" desc="The region this service runs in.">
            <ReadOnly value={region || "Not reported"} />
          </SettingsRow>
          <SettingsRow label="Project" desc="Group this service under a project & environment.">
            <MoveToProject kind="service" resourceId={serviceId} />
          </SettingsRow>
          <SettingsRow label="Instance type" desc="Container CPU & memory. Changing it is priced and confirmed first, then applied by a redeploy.">
            <InstanceType serviceId={serviceId} resources={svc.resources} currentPlanId={svc.plan_id} onChanged={onResourcesChanged} />
          </SettingsRow>
        </SettingsSection>

        {/* 2 · Build */}
        <SettingsSection id="build" title="Build">
          <SettingsRow label="Source" desc="Repository and branch this service builds from. Both are read from your connected GitHub account — switch to a test branch here, then redeploy.">
            <div className="flex flex-col gap-2">
              <GitSourcePicker
                repo={repo}
                branch={branch}
                disabled={buildBusy}
                onChange={(next) => {
                  if (next.repo !== undefined) setRepo(next.repo);
                  if (next.branch !== undefined) setBranch(next.branch);
                }}
              />
              {repo !== (svc.repo || "") && (
                <p className="text-[11.5px]" style={{ color: "var(--warn)" }}>
                  Repointing this service at a different repository. If it authenticates with a deploy
                  key bound to the old repo, add the key to the new one or the next deploy will fail.
                </p>
              )}
              <SaveInline busy={buildBusy} msg={buildMsg} onSave={saveBuild} />
            </div>
          </SettingsRow>
          <SettingsRow label="Root directory" desc="Optional. Subdirectory to build from.">
            <TextInput mono value={rootDir} onChange={setRootDir} placeholder="./" />
          </SettingsRow>
          <SettingsRow label="Build command" desc="Runs to build your service each deploy.">
            <div className="flex flex-col gap-2">
              <TextInput mono value={buildCmd} onChange={setBuildCmd} placeholder="npm ci && npm run build" />
              <SaveInline busy={buildBusy} msg={buildMsg} onSave={saveBuild} />
            </div>
          </SettingsRow>
          <SettingsRow label="Git credentials" desc="How DebutDeploy authenticates to your repo.">
            <div className="flex items-center gap-2">
              <ReadOnly value="Deploy key · debutdeploy-platform" />
              <Button variant="secondary" title="Uses the workspace app install (display only)">Use my credentials</Button>
            </div>
          </SettingsRow>
          <SettingsRow label="Build filters" desc="Paths that trigger or skip a build.">
            <div className="flex flex-col gap-3">
              <PathList label="Included paths" items={included} onChange={setIncluded} />
              <PathList label="Ignored paths" items={ignored} onChange={setIgnored} />
            </div>
          </SettingsRow>
        </SettingsSection>

        {/* 3 · Deploy */}
        <SettingsSection id="deploy" title="Deploy">
          <SettingsRow label="Pre-deploy command" desc="Optional. Runs before the release goes live.">
            <TextInput mono value={preDeploy} onChange={setPreDeploy} placeholder="npx prisma migrate deploy" />
          </SettingsRow>
          <SettingsRow label="Start command" desc="Command that starts your service.">
            <div className="flex flex-col gap-2">
              <TextInput mono value={startCmd} onChange={setStartCmd} placeholder="node dist/server.js" />
              <SaveInline busy={buildBusy} msg={buildMsg} onSave={saveBuild} />
            </div>
          </SettingsRow>
          <SettingsRow label="Auto-deploy" desc="Deploy automatically when you push to your branch.">
            <SelectInput value={autoDeploy} onChange={changeAutoDeploy} options={["On commit", "Off"]} />
          </SettingsRow>
          <SettingsRow label="Deploy hook" desc="POST to this URL to trigger a deploy.">
            <div className="flex items-center gap-2">
              <ReadOnly
                mono
                value={hookRevealed ? deployHook : "https://app.debutdepoly.com/deploy/hook/••••••••"}
              />
              <IconBtn title={hookRevealed ? "Hide" : "Reveal"} onClick={() => setHookRevealed((v) => !v)}>
                {hookRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </IconBtn>
              <IconBtn title={hookCopied ? "Copied" : "Copy"} onClick={() => navigator.clipboard?.writeText(deployHook).then(() => { setHookCopied(true); setTimeout(() => setHookCopied(false), 1500); })}>
                {hookCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </IconBtn>
              <Button variant="secondary" title="Regeneration isn't wired (display only)">
                <RefreshCw className="h-3.5 w-3.5" /> Regenerate hook
              </Button>
            </div>
          </SettingsRow>
        </SettingsSection>

        {/* 4 · Custom Domains */}
        <SettingsSection id="domains" title="Custom Domains">
          <SettingsRow label="Custom domains" desc="Domains pointed at this service, with live DNS + certificate status.">
            <div className="flex flex-col gap-3">
              <DomainsList serviceId={serviceId} refreshKey={domainsRefresh}
                onLoaded={(list) => setListedFreeHost(list.find((d) => d.free)?.host || null)} />
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" onClick={() => setDomainModal(true)}><Plus className="h-3.5 w-3.5" /> Add Custom Domain</Button>
                <Button variant="secondary" title="Re-check DNS and certificate status" onClick={() => setDomainsRefresh((n) => n + 1)}>
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </div>
          </SettingsRow>
          {domainModal && (
            <AddDomainModal
              serviceId={serviceId}
              onClose={() => setDomainModal(false)}
              onBound={() => setDomainsRefresh((n) => n + 1)}
            />
          )}
          <SettingsRow label="DebutDeploy subdomain" desc="A free subdomain always reachable for this service.">
            {freeHost ? (
              <a
                href={`https://${freeHost}`}
                target="_blank"
                rel="noreferrer"
                className="mono inline-flex items-center gap-1.5 text-[12.5px]"
                style={{ color: "var(--accent-text)" }}
              >
                {freeHost}<ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              <span className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>No free subdomain is assigned to this service.</span>
            )}
          </SettingsRow>
        </SettingsSection>

        <ObjectStorageSection serviceId={serviceId} isAdmin={user?.role === "admin"} />

        {/* 5 · PR Previews */}
        <SettingsSection id="pr-previews" title="PR Previews">
          <SettingsRow label="Pull request previews" desc="Spin up a temporary environment for each PR.">
            <SelectInput value={prPreview} onChange={setPrPreview} options={["Off", "Automatic", "Manual"]} />
          </SettingsRow>
        </SettingsSection>

        {/* 6 · Networking */}
        <SettingsSection id="networking" title="Networking">
          <SettingsRow label="Private networking" desc="Reach other services over an internal network.">
            <div
              className="flex items-start gap-3 rounded-md border px-3.5 py-3"
              style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
            >
              <Lock className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
              <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>
                Private networking is available on Pro plans. Services in the same workspace can talk to each other over a private network without exposing ports publicly.
              </p>
            </div>
          </SettingsRow>
        </SettingsSection>

        {/* 7 · Edge Caching */}
        <SettingsSection id="edge-caching" title="Edge Caching">
          <SettingsRow label="Cacheable file types" desc="Which responses the edge network may cache.">
            <SelectInput value={edgeCache} onChange={setEdgeCache} options={["None", "Static assets", "All GET"]} />
          </SettingsRow>
        </SettingsSection>

        {/* 8 · Notifications */}
        <SettingsSection id="notifications" title="Notifications">
          <SettingsRow label="Service notifications" desc="Alerts for failed deploys and downtime.">
            <SelectInput value={svcNotify} onChange={changeSvcNotify} options={["Workspace default", "Failures only", "Off"]} />
          </SettingsRow>
          <SettingsRow label="Preview-env notifications" desc="Alerts for PR preview environments.">
            <SelectInput value={previewNotify} onChange={setPreviewNotify} options={["Off", "Failures only", "All events"]} />
          </SettingsRow>
        </SettingsSection>

        {/* 9 · Log Stream */}
        <SettingsSection id="log-stream" title="Log Stream">
          <SettingsRow label="Destination" desc="Ship logs to an external endpoint.">
            <div
              className="flex flex-col gap-2 rounded-md border px-3.5 py-3"
              style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>Log endpoint</span>
                <span className="mono text-[12px]" style={{ color: "var(--text)" }}>None</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>Token</span>
                <span className="mono text-[12px]" style={{ color: "var(--text)" }}>None</span>
              </div>
              <p className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>Using workspace default.</p>
            </div>
          </SettingsRow>
        </SettingsSection>

        {/* 10 · Health Checks */}
        <SettingsSection id="health" title="Health Checks">
          <SettingsRow label="Health check path" desc="Path DebutDeploy polls to decide the service is healthy.">
            <div className="flex flex-col gap-2">
              <TextInput mono value={healthPath} onChange={setHealthPath} placeholder="/healthz" />
              <SaveInline busy={buildBusy} msg={buildMsg} onSave={saveBuild} />
            </div>
          </SettingsRow>
        </SettingsSection>

        {/* 10b · Disk */}
        <DiskSection serviceId={serviceId} />

        {/* 11 · Delete or suspend */}
        <section
          id="danger"
          className="scroll-mt-24 rounded-lg px-5 py-[18px]"
          style={{ border: "1px solid var(--err)", background: "var(--err-soft)" }}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="mb-[3px] text-[14.5px] font-semibold" style={{ color: "var(--err-text)" }}>Delete web service</h3>
              <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>
                Permanently removes the container, domain and build history. This cannot be undone.
              </p>
            </div>
            <Button variant="danger" onClick={() => setConfirmDel(true)}>
              <Trash2 className="h-3.5 w-3.5" /> Delete web service
            </Button>
          </div>
        </section>
      </div>

      {confirmDel && (
        <ConfirmDelete
          name={svc.name}
          kind="service"
          onConfirm={deleteSvc}
          onCancel={() => setConfirmDel(false)}
        />
      )}

      <div className="hidden w-[172px] shrink-0 lg:block">
        <AnchorNav items={navItems} active={activeSection} onJump={jump} />
      </div>
    </div>
  );
}

function ObjectStorageSection({ serviceId, isAdmin }) {
  const regions = ["fsn1", "nbg1", "hel1"];
  const [connection, setConnection] = useState(null);
  const [region, setRegion] = useState("fsn1");
  const [buckets, setBuckets] = useState([]);
  const [bucket, setBucket] = useState("");
  const [preset, setPreset] = useState("evidence");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    api.objectStorageStatus().then((status) => {
      if (!active) return;
      setConnection(status);
      if (status.region) setRegion(status.region);
    }).catch((e) => { if (active) setError(e.message || "Could not read Object Storage connection."); });
    return () => { active = false; };
  }, [isAdmin]);
  useEffect(() => {
    if (!connection?.configured) return;
    let active = true;
    api.objectStorageBuckets(region).then((list) => {
      if (active) { setBuckets(list); setBucket((current) => list.some((item) => item.name === current) ? current : list[0]?.name || ""); }
    }).catch((e) => { if (active) setError(e.message || "Could not load buckets."); });
    return () => { active = false; };
  }, [connection?.configured, region]);

  async function attach() {
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.attachObjectStorageBucket(bucket, { serviceUuid: serviceId, region, kind: preset });
      setMessage(`Added ${result.variables.length} environment variables to ${result.service}. Redeploy this service to apply them.`);
    } catch (e) { setError(e.message || "Could not attach the bucket."); }
    finally { setBusy(false); }
  }

  if (!isAdmin) return <SettingsSection id="object-storage" title="Object Storage">
    <SettingsRow label="S3 bucket for this service" desc="Store uploads and application files in object storage.">
      <div className="flex flex-col items-start gap-2">
        <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>Contact a DebutDeploy administrator to create and attach a bucket to this service. Customer bucket provisioning and pricing are being added; this service cannot access a bucket until one is provisioned.</p>
        <Link className="btn btn-secondary" to="/object-storage">Open S3 Storage</Link>
      </div>
    </SettingsRow>
  </SettingsSection>;

  return <SettingsSection id="object-storage" title="Object Storage">
    <SettingsRow label="Attach an S3 bucket" desc="Add bucket credentials to this service as environment variables. The service needs a redeploy before it can use them.">
      <div className="flex flex-col gap-3">
        {!connection?.configured ? <div className="flex flex-col items-start gap-2">
          <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>Connect a Hetzner S3 key and manage buckets from Object Storage.</p>
          <Link className="btn btn-secondary" to="/object-storage">Open Object Storage</Link>
        </div> : <>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs" style={{ color: "var(--text-muted)" }}>Region
              <select className="input" value={region} onChange={(e) => setRegion(e.target.value)}>{regions.map((value) => <option key={value} value={value}>{value.toUpperCase()}</option>)}</select>
            </label>
            <label className="flex flex-col gap-1 text-xs" style={{ color: "var(--text-muted)" }}>Bucket
              <select className="input" value={bucket} onChange={(e) => setBucket(e.target.value)}><option value="">Choose bucket</option>{buckets.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}</select>
            </label>
            <label className="flex flex-col gap-1 text-xs" style={{ color: "var(--text-muted)" }}>Environment variables
              <select className="input" value={preset} onChange={(e) => setPreset(e.target.value)}><option value="evidence">Multimedia evidence (EVIDENCE_S3_*)</option><option value="aws">AWS SDK (AWS_*)</option></select>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" disabled={busy || !bucket} onClick={attach}>{busy ? <Spinner /> : "Attach bucket"}</Button>
            <Link className="text-xs underline" style={{ color: "var(--accent-text)" }} to="/object-storage">Manage buckets</Link>
          </div>
        </>}
        {message && <p role="status" className="text-xs" style={{ color: "var(--ok-text)" }}>{message}</p>}
        {error && <p role="alert" className="text-xs" style={{ color: "var(--err-text)" }}>{error}</p>}
      </div>
    </SettingsRow>
  </SettingsSection>;
}

// ── Disk (persistent volumes) ───────────────────────────────────────────────────

function DiskSection({ serviceId }) {
  const [disks, setDisks] = useState(null);   // null = loading
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(false); // the priced Add Disk dialog
  const [pendingRemove, setPendingRemove] = useState(null); // disk awaiting confirmation
  const [busy, setBusy] = useState(false);    // true while removing (redeploying)

  function load() {
    setErr(null);
    api.serviceVolumes(serviceId)
      .then((d) => setDisks(Array.isArray(d) ? d : []))
      .catch((e) => { setDisks([]); setErr(e.message || "Failed to load disks"); });
  }
  useEffect(load, [serviceId]);

  const monthlyPenceEach = 10; // £0.10/GB/mo — mirrored from STORAGE_PLAN for the summary line
  const totalGb = (disks || []).reduce((s, d) => s + (d.sizeGb || 0), 0);

  async function remove() {
    const d = pendingRemove;
    if (!d || busy) return;
    setBusy(true); setErr(null);
    try {
      await api.deleteServiceVolume(serviceId, d.uuid);
      setPendingRemove(null);
      load();
    } catch (e) {
      setErr(e.message || "Failed to remove disk");
    } finally { setBusy(false); }
  }

  return (
    <SettingsSection id="disk" title="Disk">
      <SettingsRow label="Persistent disks" desc="Attach a persistent disk to keep filesystem data across deploys. Billed £0.10 per GB per month.">
        <div className="flex flex-col gap-3">
          <p className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>
            Adding or removing a disk redeploys the service. You confirm the size and cost before anything is created.
          </p>

          {busy && (
            <p className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: "var(--text-muted)" }}>
              <Spinner /> Redeploying…
            </p>
          )}

          {!disks && (
            <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>
              <Spinner className="mr-2 inline" /> Loading disks…
            </p>
          )}

          {disks && disks.length === 0 && (
            <p className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>No disks attached.</p>
          )}

          {disks && disks.length > 0 && (
            <div className="flex flex-col gap-2">
              {disks.map((d) => (
                <div
                  key={d.uuid}
                  className="flex items-center gap-3 rounded-md border px-3 py-2"
                  style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
                >
                  <HardDrive className="h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
                  <div className="min-w-0 flex-1">
                    <Mono>{d.mountPath}</Mono>
                    {d.name && <span className="ml-2 text-[11.5px]" style={{ color: "var(--text-muted)" }}>{d.name}</span>}
                  </div>
                  <span className="shrink-0 text-[12px]" style={{ color: "var(--text-muted)" }}>
                    {d.sizeGb != null
                      ? `${d.sizeGb} GB · £${((d.sizeGb * monthlyPenceEach) / 100).toFixed(2)}/mo`
                      : "size not recorded"}
                  </span>
                  <Button variant="secondary" onClick={() => setPendingRemove(d)} disabled={busy}>
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                </div>
              ))}
              <p className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>
                {totalGb} GB attached to this service — £{((totalGb * monthlyPenceEach) / 100).toFixed(2)}/mo.
              </p>
            </div>
          )}

          <div>
            <Button variant="primary" onClick={() => setAdding(true)} disabled={busy}>
              <Plus className="h-3.5 w-3.5" /> Add Disk
            </Button>
          </div>

          {err && <p className="text-xs" style={{ color: "var(--err-text)" }}>{err}</p>}
        </div>
      </SettingsRow>

      {adding && (
        <AddDiskModal serviceId={serviceId} onClose={() => setAdding(false)} onAdded={load} />
      )}

      <ConfirmDialog
        open={!!pendingRemove}
        danger
        busy={busy}
        title="Remove this disk?"
        message={pendingRemove
          ? `${pendingRemove.mountPath} (${pendingRemove.sizeGb ?? "?"} GB) will be detached and its data permanently deleted. The service redeploys, and billing for these GB stops — you are credited pro rata for the rest of this cycle.`
          : ""}
        confirmLabel="Remove disk"
        onConfirm={remove}
        onCancel={() => setPendingRemove(null)}
      />
    </SettingsSection>
  );
}

// ── Custom domains manager (list + DNS / Routing / Certificate status per domain) ──

// ok: true = good, false = known bad, null/undefined = not known yet (nothing to check, or
// an older server that doesn't report it).
function DomainBadge({ ok, okLabel, pendingLabel, badLabel, title }) {
  const bad = ok === false && !!badLabel;
  return (
    <span title={title} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{
        background: ok ? "var(--ok-soft)" : bad ? "var(--warn-soft)" : "var(--surface-2)",
        color: ok ? "var(--ok-text)" : bad ? "var(--warn-text)" : "var(--text-muted)",
      }}>
      {ok ? <Check className="h-3 w-3" /> : bad ? <AlertTriangle className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
      {ok ? okLabel : bad ? badLabel : pendingLabel}
    </span>
  );
}

// dns.warnings code → one plain sentence. Unknown codes (and unknown_server_ip) say nothing.
const DNS_WARNINGS = {
  extra_a: "There is more than one A record for this name and one of them points somewhere else — remove the extra one.",
  aaaa: "An IPv6 (AAAA) record exists and will break certificate issuance — remove it.",
  caa_blocks_letsencrypt: "A CAA record doesn't allow Let's Encrypt to issue certificates for this domain — add letsencrypt.org to it, or remove it.",
};

// One view of a domain row that works against the new contract and an older server that
// only sends the legacy verified / certIssued booleans.
function domainView(d) {
  const dnsOk = typeof d.dns?.ok === "boolean" ? d.dns.ok : !!d.verified;
  const certOk = d.cert && typeof d.cert.ok === "boolean" ? d.cert.ok : !!d.certIssued;
  const state = d.state || (!dnsOk ? "waiting_dns" : certOk ? "active" : "cert_pending");
  // No routing verdict (older server, or nothing to probe yet): a working domain is routed.
  const routed = typeof d.routed === "boolean" ? d.routed : state === "active" ? true : null;
  const ips = Array.isArray(d.dns?.ips) ? d.dns.ips : Array.isArray(d.resolvedIps) ? d.resolvedIps : [];
  const warnings = (Array.isArray(d.dns?.warnings) ? d.dns.warnings : []).map((w) => DNS_WARNINGS[w]).filter(Boolean);
  const expires = d.cert?.expiresAt ? new Date(d.cert.expiresAt) : null;
  const certTitle = certOk && expires && !Number.isNaN(expires.getTime())
    ? `Certificate valid until ${expires.toLocaleDateString()}${d.cert.issuer ? ` · ${d.cert.issuer}` : ""}`
    : undefined;
  return { dnsOk, certOk, routed, state, ips, warnings, certTitle };
}

const DOMAIN_POLL_MS = 20000;

function DomainsList({ serviceId, refreshKey, onLoaded }) {
  const [domains, setDomains] = useState(null);
  const [busy, setBusy] = useState(null);     // host being removed
  const [acting, setActing] = useState(null); // host with a connect / retry in flight
  const [notes, setNotes] = useState({});     // host → { ok, text } result of the last action

  // Latest-wins + unmount guards: a slow answer must not overwrite a newer one, repaint a
  // different service's list, or set state on a list that is gone.
  const seq = useRef(0);
  const alive = useRef(true);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // quiet = background refresh: keep showing the current rows, and keep them if it fails.
  function load(quiet) {
    const n = ++seq.current;
    return api.listDomains(serviceId)
      .then((d) => {
        if (!alive.current || n !== seq.current) return;
        const list = Array.isArray(d) ? d.filter((x) => x && x.host) : [];
        setDomains(list);
        onLoadedRef.current?.(list);
      })
      .catch(() => { if (alive.current && n === seq.current && !quiet) setDomains([]); });
  }
  useEffect(() => { setDomains(null); setNotes({}); load(false); }, [serviceId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // While anything is still settling, re-check quietly — no spinner, and not while the
  // browser tab is hidden. Coming back to the tab re-checks straight away.
  const settling = !!domains?.some((d) => domainView(d).state !== "active");
  useEffect(() => {
    if (!settling) return;
    const tick = () => { if (document.visibilityState === "visible") load(true); };
    const timer = setInterval(tick, DOMAIN_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", tick); };
  }, [settling, serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function remove(host) {
    if (!window.confirm(`Remove ${host} from this service? Removing a root domain also removes its www alias.`)) return;
    setBusy(host);
    try { await api.removeDomain(serviceId, host); await load(true); }
    catch (e) { alert(e.message || "Remove failed"); }
    finally { if (alive.current) setBusy(null); }
  }

  // Connect anyway (force-bind) and the two retry buttons share one in-flight slot per list.
  async function act(host, fn) {
    if (acting) return;
    setActing(host);
    try {
      const r = await fn();
      if (!alive.current) return;
      const text = r?.deploy === "deferred"
        ? "Saved — a deploy is already in progress, so this applies with the next one."
        : "Redeploy started — this usually takes a minute or two.";
      setNotes((m) => ({ ...m, [host]: { ok: true, text } }));
      await load(true);
    } catch (e) {
      if (alive.current) setNotes((m) => ({ ...m, [host]: { ok: false, text: e.message || "That didn't work — try again." } }));
    } finally { if (alive.current) setActing(null); }
  }
  const connectAnyway = (host) => act(host, () => api.addDomain(serviceId, host, { force: true }));
  const retry = (host) => act(host, () => api.retryDomain(serviceId, host));

  if (domains === null) return <div className="flex items-center gap-2 text-[13px]" style={{ color: "var(--text-muted)" }}><Spinner /> Loading domains…</div>;
  if (!domains.length) return <div className="text-[13px]" style={{ color: "var(--text-muted)" }}>No custom domains yet — add one below.</div>;
  return (
    <div className="rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}>
      {domains.map((d, i) => {
        const v = domainView(d);
        const working = acting === d.host;
        const note = notes[d.host];
        return (
          <div key={d.host} className="px-3.5 py-2.5"
            style={{ borderTop: i ? "1px solid var(--border)" : "none" }}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <a href={`https://${d.host}`} target="_blank" rel="noreferrer" className="mono inline-flex items-center gap-1.5 text-[13px] font-medium" style={{ color: "var(--accent-text)" }}>
                {d.host}{d.free && <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>free</span>}<ExternalLink className="h-3 w-3" />
              </a>
              <div className="flex flex-wrap items-center gap-2">
                <DomainBadge ok={v.dnsOk} okLabel="DNS" pendingLabel="DNS pending" />
                <DomainBadge ok={v.routed} okLabel="Routing" pendingLabel="Routing pending" badLabel="Not routed" />
                <DomainBadge ok={v.certOk} okLabel="Certificate" pendingLabel="Cert pending" title={v.certTitle} />
                {/* The platform's own hostname is not removable. */}
                {!d.free && (
                  <button onClick={() => remove(d.host)} disabled={busy === d.host} title="Remove domain"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--err)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}>
                    {busy === d.host ? <Spinner /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>

            {v.state !== "active" && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]" style={{ color: "var(--warn-text)" }}>
                {v.state === "waiting_dns" && (
                  <>
                    <span>
                      DNS isn't pointing at this service yet — it {v.ips.length ? <>resolves to <Mono>{v.ips.join(", ")}</Mono></> : "doesn't resolve to anything"}
                      {d.serverIp ? <>, and should point to <Mono>{d.serverIp}</Mono></> : null}.
                      {d.pending ? " It will be connected automatically once DNS is correct." : ""}
                    </span>
                    {d.pending && !d.free && (
                      <Button variant="secondary" disabled={!!acting} onClick={() => connectAnyway(d.host)}
                        title="Bind the domain now without waiting for DNS. The certificate can't be issued until DNS points here.">
                        {working ? <Spinner /> : null} Connect anyway
                      </Button>
                    )}
                  </>
                )}
                {v.state === "not_routed" && (
                  <>
                    <span>DNS is correct, but the service isn't serving this domain yet. A redeploy fixes that.</span>
                    <Button variant="secondary" disabled={!!acting} onClick={() => retry(d.host)}>
                      {working ? <Spinner /> : <RotateCw className="h-3.5 w-3.5" />} Redeploy to fix
                    </Button>
                  </>
                )}
                {v.state === "cert_pending" && (
                  <>
                    <span>DNS and routing are fine; the certificate hasn't been issued yet. This usually takes a few minutes — retry if it stays stuck.</span>
                    <Button variant="secondary" disabled={!!acting} onClick={() => retry(d.host)}>
                      {working ? <Spinner /> : <RotateCw className="h-3.5 w-3.5" />} Retry certificate
                    </Button>
                  </>
                )}
                {v.state === "unreachable" && (
                  <span>This domain didn't answer on HTTPS. Check the service is running — it's re-checked automatically.</span>
                )}
              </div>
            )}
            {v.warnings.map((w) => (
              <div key={w} className="mt-1 flex items-start gap-1.5 text-[12px]" style={{ color: "var(--warn-text)" }}>
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}
              </div>
            ))}
            {note && <div className="mt-1 text-[12px]" style={{ color: note.ok ? "var(--text-muted)" : "var(--err-text)" }}>{note.text}</div>}

            {!d.free && <DnsSetup domain={d.host} kind="hosting" serviceId={serviceId} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Instance type (real Docker resource limits, editable) ──────────────────────

const CPU_OPTS = [
  { v: "0", label: "Shared (no limit)" },
  { v: "0.5", label: "0.5 vCPU" },
  { v: "1", label: "1 vCPU" },
  { v: "2", label: "2 vCPU" },
];
const MEM_OPTS = [
  { v: "0", label: "No limit" },
  { v: "256M", label: "256 MB" },
  { v: "512M", label: "512 MB" },
  { v: "1G", label: "1 GB" },
  { v: "2G", label: "2 GB" },
];
const fmtCpu = (v) => (v === "0" || v == null ? "Shared" : `${v} vCPU`);
const fmtMem = (v) => (v === "0" || v == null ? "No limit" : String(v).replace(/M$/, " MB").replace(/G$/, " GB"));

// plan.ramGb → a Docker limits_memory string ("512M" / "1G"); and a match test so
// we can preselect the plan whose derived limits equal the service's current ones.
const ramToDocker = (gb) => (gb < 1 ? `${Math.round(gb * 1024)}M` : `${gb}G`);
const planMatches = (p, cpus, memory) => String(p.vcpuCount) === String(cpus) && ramToDocker(p.ramGb) === String(memory);

function InstanceType({ serviceId, resources, currentPlanId, onChanged }) {
  const cur = { cpus: String(resources?.cpus ?? "0"), memory: String(resources?.memory ?? "0") };
  const [plans, setPlans] = useState(null);   // null=loading; [] on failure
  const [sel, setSel] = useState(currentPlanId || "");
  const [savedPlanId, setSavedPlanId] = useState(currentPlanId || null);
  const [cpus, setCpus] = useState(cur.cpus); // custom-mode raw limits
  const [memory, setMemory] = useState(cur.memory);
  const [confirming, setConfirming] = useState(false); // the priced confirmation dialog
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let off = false;
    setSavedPlanId(currentPlanId || null);
    api.plans().then((d) => {
      if (off) return;
      const compute = d?.compute || [];
      setPlans(compute);
      const match = compute.find((p) => planMatches(p, cur.cpus, cur.memory));
      setSel(currentPlanId || match?.id || compute[0]?.id || "");
    }).catch(() => { if (!off) { setPlans([]); setSel(""); } });
    return () => { off = true; };
  }, [serviceId, currentPlanId]);

  const plan = (plans || []).find((p) => p.id === sel) || null;
  const effCpus = plan ? String(plan.vcpuCount) : cur.cpus;
  const effMem = plan ? ramToDocker(plan.ramGb) : cur.memory;
  const dirty = !!plan && (plan.id !== savedPlanId || effCpus !== cur.cpus || effMem !== cur.memory);
  const cpuOpts = CPU_OPTS.some((o) => o.v === cpus) ? CPU_OPTS : [{ v: cpus, label: fmtCpu(cpus) }, ...CPU_OPTS];
  const memOpts = MEM_OPTS.some((o) => o.v === memory) ? MEM_OPTS : [{ v: memory, label: fmtMem(memory) }, ...MEM_OPTS];

  // Saving opens the confirmation dialog rather than writing straight through: a resize
  // costs money and restarts the container, so the customer confirms the new spec, the
  // new price, the pro-rata amount and the redeploy first. The dialog owns the apply.
  function applied(r) {
    const deploy = r.steps.find((s) => s.step === "redeploy");
    setMsg({
      ok: true,
      text: deploy?.ok
        ? `Applied — ${r.preview.service.name} is redeploying with the new limits.`
        : "Applied. Redeploy the service to bring the new limits into effect.",
    });
    // Lift the new limits so the page (and this control's "dirty" check) reflect reality.
    onChanged?.({ cpus: r.preview.to.cpus, memory: r.preview.to.memory });
    setSavedPlanId(plan?.id || null);
  }

  if (plans == null) {
    return <div className="rounded-md border px-3.5 py-3 text-[13px]" style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Loading plans…</div>;
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border px-3.5 py-3"
      style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase" style={{ color: "var(--text-muted)", letterSpacing: ".04em" }}>Plan</span>
          <select className="input" value={sel} onChange={(e) => setSel(e.target.value)} style={{ minWidth: 240 }}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {fmtHourly(p.priceMo)} (about ${p.priceMo}/mo) · {p.vcpu} · {p.ram}</option>
            ))}
          </select>
        </label>
        <Button variant="secondary" onClick={() => setConfirming(true)} disabled={!dirty || !plan}>Save</Button>
      </div>

      {confirming && (
        <ResourceChangeModal
          serviceId={serviceId}
          planId={plan ? plan.id : null}
          cpus={effCpus}
          memory={effMem}
          onClose={() => setConfirming(false)}
          onApplied={applied}
        />
      )}
      {/* Price populates the moment a plan is chosen (Render-style). */}
      {plan ? (
        <p className="text-[12.5px]" style={{ color: "var(--text)" }}>
          <span className="font-bold">{fmtHourly(plan.priceMo)}</span>
          <span style={{ color: "var(--text-muted)" }}> · about ${plan.priceMo}/mo, billed daily for the hours it runs</span>
          <span style={{ color: "var(--text-muted)" }}> · {plan.vcpu} · {plan.ram} · {plan.disk}
            {plan.renderMo ? ` — ${Math.round((1 - plan.priceMo / plan.renderMo) * 100)}% under Render ($${plan.renderMo})` : ""}
          </span>
        </p>
      ) : (
        <p className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>Choose a paid plan to apply a priced resource size.</p>
      )}
      {msg && <p className="text-[11.5px]" style={{ color: msg.ok ? "var(--ok-text)" : "var(--err-text)" }}>{msg.text}</p>}
    </div>
  );
}

// ── Settings field primitives ───────────────────────────────────────────────────

function ReadOnly({ value, mono }) {
  return (
    <div
      className={`${mono ? "mono " : ""}truncate rounded-md border px-3 py-[9px] text-[13px]`}
      style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text-muted)" }}
      title={value}
    >
      {value}
    </div>
  );
}

// Inline-editable name field (Render-style): read-only + Edit → input + Save/Cancel.
function EditableName({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function start() { setDraft(value); setErr(null); setEditing(true); }

  async function save() {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true); setErr(null);
    try {
      await onSave(name);
      setEditing(false);
    } catch (e) {
      setErr(e.message || "Rename failed");
    } finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <ReadOnly value={value} />
        <Button variant="secondary" onClick={start}>Edit</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          className="input"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        />
        <Button variant="primary" onClick={save} disabled={busy || !draft.trim()}>{busy ? <Spinner /> : "Save"}</Button>
        <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
      </div>
      {err && <span className="text-xs" style={{ color: "var(--err-text)" }}>{err}</span>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, mono }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${mono ? "mono " : ""}input`}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function SaveInline({ busy, msg, onSave }) {
  return (
    <div className="flex items-center gap-3">
      <Button type="button" variant="primary" onClick={onSave} disabled={busy}>{busy ? <Spinner /> : "Save"}</Button>
      {msg && <span className="text-xs" style={{ color: msg.ok ? "var(--ok-text)" : "var(--err-text)" }}>{msg.text}</span>}
    </div>
  );
}

function IconBtn({ children, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-md border transition-colors"
      style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text-muted)" }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
    >
      {children}
    </button>
  );
}

// dashed "Add path" list — client-side local state only (display only)
function PathList({ label, items, onChange }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <p className="mb-1.5 text-[11.5px] font-semibold" style={{ color: "var(--text-muted)" }}>{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        {items.map((p, i) => (
          <span
            key={p + i}
            className="mono inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px]"
            style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text)" }}
          >
            {p}
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} style={{ color: "var(--text-muted)" }}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {val !== null && (
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && val.trim()) { e.preventDefault(); onChange([...items, val.trim()]); setVal(""); }
            }}
            placeholder="Add path…"
            className="mono rounded-md border border-dashed bg-transparent px-2.5 py-1 text-[12px] outline-none"
            style={{ borderColor: "var(--border-strong)", color: "var(--text)", width: "140px" }}
          />
        )}
      </div>
    </div>
  );
}
